import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import studentService from '../../services/studentService';
import { useStudentProgress } from '../../hooks/useStudentProgress';
import { isToday, formatDate } from '../../utils/dateUtils';
import { getAssignmentTitle } from '../../utils/assignmentUtils';
import api from '../../api/api';
import '../../styles/students/StudentProfilePage.css';
import '../../styles/students/StudentAssignmentCardFix.css';

const StudentProfilePage = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [studentParents, setStudentParents] = useState([]);
  const [learningPlans, setLearningPlans] = useState([]);
  const [learningPlanLoading, setLearningPlanLoading] = useState(true);
  const [expandedPlans, setExpandedPlans] = useState(new Set());

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Get studentId from either params or location state
  const studentId = id || (location.state && location.state.studentId);
  const studentName = location.state && location.state.studentName;
  
  // Get class context from location state for filtering (when navigating from class context)
  const contextHalaqaId = location.state && location.state.halaqaId;
  const contextHalaqaName = location.state && location.state.halaqaName;
  
  // Use the student progress hook to get all the progress metrics
  const { 
    dashboardStats,
    loading: progressLoading
  } = useStudentProgress(studentId, contextHalaqaId);

  useEffect(() => {
    if (!studentId) {
      console.error('No student ID provided');
      navigate('/classes');
      return;
    }

    console.log('Fetching data for student ID:', studentId);

    const fetchStudentData = async () => {
      try {
        console.log('=== FETCHSTUDENTDATA STARTED ===');
        console.log('contextHalaqaId:', contextHalaqaId);
        console.log('contextHalaqaName:', contextHalaqaName);
        setLoading(true);
        
        // Initialize student object with basic structure
        let studentObj = {
          _id: studentId,
          name: studentName || 'Loading...',
          email: '',
          pendingAssessments: [],
          notes: ''
        };
        
        try {
          // First try to get basic student data
          console.log('Fetching basic student data for ID:', studentId);
          const studentData = await studentService.getStudentProfile(studentId);
          console.log('Student data received:', studentData);
          
          // Update student object with retrieved data (preserve existing pendingAssessments)
          studentObj = {
            _id: studentData._id || studentId,
            name: studentData.name || 'Error Loading Name',
            email: studentData.email || '',
            studentInfo: studentData.studentInfo, // Include studentInfo for multi-class check
            pendingAssessments: studentObj.pendingAssessments, // Preserve existing pending assessments
            notes: studentData.notes || ''
          };
          
          // Finally try to get assignments data
          try {
            console.log('Fetching assignments for student ID:', studentId);
            
            // Build parameters for assignment fetching
            const assignmentParams = {};
            if (contextHalaqaId) {
              assignmentParams.halaqaId = contextHalaqaId;
              console.log(`Filtering assignments by class context: ${contextHalaqaName} (${contextHalaqaId})`);
            }
            
            const assignmentsData = await studentService.getStudentAssignments(studentId, assignmentParams);
            console.log('Assignments data received:', assignmentsData);
            
            if (assignmentsData && assignmentsData.length > 0) {
              console.log('Raw assignments for pending assessment filtering:', assignmentsData);
              
              // Filter for pending assignments due today
              const pendingAssignments = assignmentsData
                .filter(assignment => {
                  const hasAssessment = assignment.assessment && assignment.assessment.rating;
                  const isDueToday = isToday(assignment.dueDate);
                  
                  console.log(`Assignment ${assignment.title || assignment._id}:`, {
                    hasAssessment,
                    rating: assignment.assessment?.rating,
                    dueDate: assignment.dueDate,
                    isDueToday,
                    willInclude: !hasAssessment && isDueToday
                  });
                  
                  // Filter by: not assessed yet AND due date is today
                  return !hasAssessment && isDueToday;
                })
                .map(assignment => {
                  // Get the content using assignment utility (handles both legacy and new formats)
                  const title = getAssignmentTitle(assignment);
                  
                  // Priority color based on due date - orange (today) by default
                  // Using the same color system as TeacherDashboard
                  let indicatorColor = '#ff9800'; // Orange for due today
                  
                  // Format the date for display
                  const dueDate = assignment.dueDate
                    ? formatDate(assignment.dueDate)
                    : 'Today';
                  
                  return {
                    _id: assignment._id || `temp-${Math.random()}`,
                    id: assignment._id,
                    title: title,
                    dueDate: dueDate,
                    type: assignment.type || 'revision',
                    indicatorColor
                  };
                });
              
              studentObj.pendingAssessments = pendingAssignments;
              console.log(`Found ${pendingAssignments.length} pending assessments for today`);
              console.log('Final pending assessments array:', pendingAssignments);
            }
          } catch (assignmentsError) {
            console.error('Error fetching assignments for student:', assignmentsError);
            // Continue with empty assignments
          }
          
        } catch (studentDataError) {
          console.error('Error fetching student data:', studentDataError);
        }
        
        // Set the final student data
        console.log('Setting final student data:', studentObj);
        setStudent(studentObj);
        setNotes(studentObj.notes || '');
        setLoading(false);
      } catch (error) {
        console.error('Fatal error in fetchStudentData:', error);
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId, studentName, navigate, contextHalaqaId, contextHalaqaName]);

  // Fetch student parents
  useEffect(() => {
    const fetchStudentParents = async () => {
      if (!studentId) return;

      try {
        const response = await fetch(`/api/students/${studentId}/parents`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setStudentParents(data.data || []);
        } else {
          console.error('Failed to fetch student parents:', response.status);
        }
      } catch (error) {
        console.error('Error fetching student parents:', error);
      }
    };

    fetchStudentParents();
  }, [studentId]);

  // Fetch student's active learning plans
  useEffect(() => {
    const fetchLearningPlans = async () => {
      if (!studentId) return;

      try {
        setLearningPlanLoading(true);
        const response = await api.get(`/learning-plans/student/${studentId}`);
        const plans = response.data.data || [];

        // Filter for active plans only
        const activePlans = plans.filter(plan => plan.status === 'active');

        // Enrich each plan with average rating
        const enrichedPlans = await Promise.all(
          activePlans.map(async (plan) => {
            try {
              const assignmentsResponse = await api.get(`/learning-plans/${plan._id}/assignments`);
              const assignments = assignmentsResponse.data.data || [];

              // Calculate average rating (excluding "Need to Repeat")
              const getRatingValue = (rating) => {
                const ratingMap = {
                  'Excellent': 5,
                  'Very Good': 4,
                  'Good': 3,
                  'Pass': 2,
                  'Weak': 1
                };
                return ratingMap[rating] || 0;
              };

              const ratedAssignments = assignments.filter(a =>
                a.assessment &&
                a.assessment.rating &&
                a.assessment.rating !== 'Need to Repeat'
              );

              if (ratedAssignments.length > 0) {
                let totalRating = 0;
                ratedAssignments.forEach(assignment => {
                  totalRating += getRatingValue(assignment.assessment.rating);
                });

                plan.averageRating = (totalRating / ratedAssignments.length).toFixed(1);
                plan.ratedAssignmentsCount = ratedAssignments.length;
              }

              return plan;
            } catch (assignmentsError) {
              console.error('Error fetching plan assignments for rating:', assignmentsError);
              // Return plan without average rating
              return plan;
            }
          })
        );

        setLearningPlans(enrichedPlans);

        // Set default expanded state: expand all if only 1 plan, collapse all if 2+
        if (enrichedPlans.length === 1) {
          setExpandedPlans(new Set([enrichedPlans[0]._id]));
        } else {
          setExpandedPlans(new Set());
        }
      } catch (error) {
        console.error('Error fetching learning plans:', error);
        setLearningPlans([]);
      } finally {
        setLearningPlanLoading(false);
      }
    };

    fetchLearningPlans();
  }, [studentId]);

  const handleSaveNotes = async (text) => {
    setNotes(text);
    setSavingNotes(true);
    
    try {
      // Try to save notes through API
      await studentService.updateStudentNotes(studentId, text);
      console.log('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      // Continue execution even if API fails - we've already updated the UI
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAssignWork = () => {
    // Use context halaqaId if navigating from class context, otherwise use student's primary halaqaId
    const halaqaId = contextHalaqaId || student?.studentInfo?.halaqaId;
    const halaqaName = contextHalaqaName || student?.studentInfo?.halaqaName || 'Unknown Class';
    
    navigate('/assignments/new', {
      state: {
        studentId: student._id || studentId,
        studentName: student.name,
        halaqaId: halaqaId,
        halaqaName: halaqaName
      }
    });
  };

  const handleContactParent = () => {
    navigate('/messages/new', {
      state: {
        recipientType: 'parent',
        studentId: student._id || studentId,
        studentName: student.name
      }
    });
  };

  const togglePlanExpansion = (planId) => {
    setExpandedPlans(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  if (loading || progressLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading student profile...</p>
      </div>
    );
  }

  return (
    <div className="student-profile-page">
      <div className="page-header">
        <h1>Student Profile</h1>
        <div className="header-actions">
          <button 
            onClick={() => navigate(-1)} 
            className="back-button"
          >
            ← Back
          </button>
          <Link to="/dashboard" className="dashboard-link">
            Dashboard
          </Link>
        </div>
      </div>

      {/* Student Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          {student.name?.charAt(0) || '?'}
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{student.name}</h2>
          {contextHalaqaId && contextHalaqaName && 
           student?.studentInfo?.halaqas?.length > 1 && (
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginTop: '4px',
              padding: '4px 8px',
              backgroundColor: '#f0f7ff',
              borderRadius: '4px',
              border: '1px solid #cce7ff'
            }}>
              Viewing in {contextHalaqaName} context
            </div>
          )}
        </div>
      </div>

      {/* Parent Information Section */}
      {studentParents.length > 0 && (
        <div className="parent-info-section" style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <h3 style={{ 
            margin: '0 0 15px 0', 
            fontSize: '18px', 
            color: '#333',
            borderBottom: '2px solid #4a90e2',
            paddingBottom: '8px'
          }}>
            Parent/Guardian Information ({studentParents.length})
          </h3>
          <div className="parents-grid" style={{
            display: 'grid',
            gridTemplateColumns: studentParents.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '15px'
          }}>
            {studentParents.map((parent, index) => (
              <div key={parent._id} className="parent-info-card" style={{
                padding: '15px',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                backgroundColor: '#f9f9f9'
              }}>
                <div className="parent-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <div className="parent-avatar" style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    marginRight: '12px'
                  }}>
                    {parent.name?.charAt(0) || 'P'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>
                      {parent.name}
                    </div>
                    {studentParents.length > 1 && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {index === 0 ? 'Primary Parent' : 'Guardian'}
                      </div>
                    )}
                  </div>
                </div>
                <div className="parent-contact" style={{ fontSize: '14px', color: '#666' }}>
                  {parent.email && (
                    <div style={{ marginBottom: '4px' }}>
                      📧 <a href={`mailto:${parent.email}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
                        {parent.email}
                      </a>
                    </div>
                  )}
                  {parent.phone && (
                    <div>
                      📞 <a href={`tel:${parent.phone}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
                        {parent.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Summary Section */}
      <h2 className="section-title">Progress Summary</h2>
      <div className="stats-container" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px 10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        margin: '0 0 20px 0',
        overflow: 'hidden'
      }}>
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assignmentCompletion}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Assignments</div>
          <div className="stat-sublabel" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            {dashboardStats.completedAssignments || 0} of {dashboardStats.totalAssignments || 0} completed
          </div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assessmentAverage.value}/5</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Avg. Rating</div>
          <div className="stat-sublabel" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            Based on {dashboardStats.assessedAssignments || 0} assessed assignments
          </div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.attendanceRate}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>Attendance</div>
          <div className="stat-sublabel" style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
            Present: {dashboardStats.presentDays || 0}/{dashboardStats.totalDays || 0} days
          </div>
        </div>
      </div>

      {/* Learning Plans Section */}
      {!learningPlanLoading && (
        <>
          <h2 className="section-title">Learning Plans {learningPlans.length > 0 && `(${learningPlans.length})`}</h2>
          {learningPlans.length > 0 ? (
            learningPlans.map((learningPlan) => {
              const isExpanded = expandedPlans.has(learningPlan._id);
              const overdue = learningPlan.progress?.due || 0;
              const scheduleStatus = overdue === 0
                ? { text: 'On Track', icon: '✓', color: '#4caf50' }
                : overdue >= 3
                ? { text: 'Behind Schedule', icon: '⚠', color: '#f44336' }
                : { text: 'Slightly Behind', icon: '⚠', color: '#ff9800' };

              return (
            <div key={learningPlan._id} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0'
            }}>
              {/* Clickable Header for Expand/Collapse */}
              <div
                onClick={() => togglePlanExpansion(learningPlan._id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: isExpanded ? '15px' : '12px',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: '0', fontSize: '18px', color: '#333' }}>
                      {learningPlan.goal.type === 'pages' && 'Pages Goal'}
                      {learningPlan.goal.type === 'verses' && 'Verses Goal'}
                      {learningPlan.goal.type === 'lines' && 'Lines Goal'}
                      {learningPlan.goal.type === 'juz' && `Juz ${learningPlan.goal.juz?.number}`}
                      {learningPlan.goal.type === 'surahs' && 'Surahs Goal'}
                    </h3>

                    {/* Assignment Type Badge */}
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      textTransform: 'capitalize'
                    }}>
                      {learningPlan.assignmentType ? learningPlan.assignmentType.replace('-', ' ') : 'New Memorization'}
                    </span>

                    {/* Average Rating */}
                    {learningPlan.averageRating && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '12px'
                        }}
                        title={`Based on ${learningPlan.ratedAssignmentsCount || 0} assessed assignment${learningPlan.ratedAssignmentsCount !== 1 ? 's' : ''}`}
                      >
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                          {learningPlan.averageRating}/5
                        </span>
                        <span style={{ fontSize: '11px', color: '#666' }}>
                          Avg
                        </span>
                      </div>
                    )}

                    {/* Progress % */}
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#4a90e2' }}>
                      {learningPlan.progress?.percent || 0}%
                    </span>

                    {/* Schedule Status Chip */}
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: scheduleStatus.color === '#4caf50' ? '#e8f5e9' : scheduleStatus.color === '#ff9800' ? '#fff3e0' : '#ffebee',
                      borderRadius: '12px'
                    }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: scheduleStatus.color }}>
                        {scheduleStatus.icon} {scheduleStatus.text}
                      </span>
                    </div>
                  </div>

                  {/* Goal Summary */}
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
                    {learningPlan.goal.type === 'pages' && `Pages ${learningPlan.goal.pages?.from}-${learningPlan.goal.pages?.to}`}
                    {learningPlan.goal.type === 'verses' && `${learningPlan.goal.verses?.total || 0} verses`}
                    {learningPlan.goal.type === 'lines' && `~${learningPlan.goal.lines?.dailyLines || 0} lines per day`}
                    {learningPlan.goal.type === 'juz' && `${learningPlan.goal.juz?.versesCount || 0} verses`}
                    {learningPlan.goal.type === 'surahs' && `${learningPlan.goal.surahs?.length || 0} surahs`}
                    {learningPlan.goal.type === 'verses' && learningPlan.goal.verses?.startFrom && learningPlan.goal.verses?.endAt && (
                      <span style={{ color: '#999', fontSize: '13px', marginLeft: '8px' }}>
                        ({learningPlan.goal.verses.startFrom.surahName || `Surah ${learningPlan.goal.verses.startFrom.surah}`} {learningPlan.goal.verses.startFrom.verse} → {learningPlan.goal.verses.endAt.surahName || `Surah ${learningPlan.goal.verses.endAt.surah}`} {learningPlan.goal.verses.endAt.verse})
                      </span>
                    )}
                    {learningPlan.goal.type === 'lines' && learningPlan.goal.lines?.startFrom && (
                      <span style={{ color: '#999', fontSize: '13px', marginLeft: '8px' }}>
                        (Starting from {learningPlan.goal.lines.startFrom.surahName || `Surah ${learningPlan.goal.lines.startFrom.surah}`}, Verse {learningPlan.goal.lines.startFrom.verse})
                      </span>
                    )}
                  </div>
                </div>

                {/* Expand/Collapse Icon */}
                <div style={{
                  fontSize: '24px',
                  color: '#666',
                  marginLeft: '12px',
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }}>
                  ▼
                </div>
              </div>

              {/* View Full Plan Button - Always Visible */}
              <div style={{ marginBottom: isExpanded ? '15px' : '0' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering collapse/expand
                    navigate(`/learning-plans/${learningPlan._id}`, {
                      state: {
                        fromStudentProfile: true,
                        studentId: student._id || studentId,
                        studentName: student.name,
                        contextHalaqaId,
                        contextHalaqaName
                      }
                    });
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4a90e2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                >
                  View Full Plan →
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
              <>

              {/* Progress Bar */}
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Progress</span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#4a90e2' }}>
                    {learningPlan.progress?.percent || 0}%
                  </span>
                </div>
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${learningPlan.progress?.percent || 0}%`,
                    height: '100%',
                    backgroundColor: learningPlan.progress?.percent >= 75 ? '#4caf50' : learningPlan.progress?.percent >= 50 ? '#2196f3' : learningPlan.progress?.percent >= 25 ? '#ff9800' : '#f44336',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '12px', color: '#888' }}>
                  <span>{learningPlan.progress?.completed || 0} of {learningPlan.progress?.total || 0} assignments completed</span>
                  <span>{(learningPlan.progress?.total || 0) - (learningPlan.progress?.completed || 0)} remaining</span>
                </div>

                {/* Overdue Details (if applicable) */}
                {overdue > 0 && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 10px',
                    backgroundColor: '#fff3e0',
                    borderLeft: `3px solid ${scheduleStatus.color}`,
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#666'
                  }}>
                    {overdue} overdue assignment{overdue !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Schedule Info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Schedule</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule.daysPerWeek} days/week
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>Days</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {learningPlan.schedule.specificDays.map(d => d.substring(0, 3)).join(', ')}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#999', marginBottom: '3px' }}>End Date</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                    {formatDate(learningPlan.timeline.end)}
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
              );
            })
          ) : (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '30px 20px',
              marginBottom: '20px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
                No active learning plans
              </p>
              <button
                onClick={() => navigate('/learning-plans/new', {
                  state: {
                    studentId: student._id || studentId,
                    studentName: student.name
                  }
                })}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                + Create Learning Plan
              </button>
            </div>
          )}
        </>
      )}

      {/* Pending Assessments Section */}
      <div className="section-header">
        <h2 className="section-title">Pending Assessments For Today</h2>
      </div>
      
      <div className="assignments-list">
        {student.pendingAssessments && student.pendingAssessments.length > 0 ? (
          student.pendingAssessments.map((assignment) => (
            <div key={assignment._id || assignment.id} className="assignment-card">
              <div 
                className="assignment-indicator" 
                style={{ backgroundColor: assignment.indicatorColor }}
              ></div>
              
              <div className="assignment-content">
                <div className="assignment-header">
                  <h3 className="assignment-title">{assignment.title}</h3>
                  <div className="assignment-type-badge">
                    {assignment.type === 'new-memorization' ? 'New Memorization' :
                     assignment.type === 'revision' ? 'Revision' :
                     assignment.type === 'recitation' ? 'Recitation' : assignment.type}
                  </div>
                </div>
                
                <div className="assignment-details">
                  <p className="assignment-date">
                    Due: {assignment.dueDate}
                  </p>
                </div>

                <div className="assessment-actions">
                  <button 
                    className="assess-btn"
                    onClick={() => navigate(`/assignments/${assignment.id}/assess`, {
                      state: {
                        studentId: student._id || studentId,
                        assignmentId: assignment.id,
                        studentName: student.name,
                        assignmentDetails: assignment.title
                      }
                    })}
                  >
                    Assess
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-assignments">
            <p>No pending assessments for today.</p>
          </div>
        )}
      </div>

      {/* Performance Links Section */}
      <h2 className="section-title">Performance</h2>
      <div className="performance-container">
        <Link 
          to={`/assignments/student/${student._id || studentId}`}
          className="performance-link"
          state={{ 
            studentId: student._id || studentId, 
            studentName: student.name,
            halaqaId: contextHalaqaId,
            halaqaName: contextHalaqaName
          }}
        >
          <h3 className="performance-link-title">Assignments</h3>
          <p className="performance-link-subtitle">
            {contextHalaqaId ? `View assignments for this student in ${contextHalaqaName}` : 'View all assignments'}
          </p>
        </Link>
        
        <div className="performance-divider"></div>
        
        <Link 
          to={`/students/${student._id || studentId}/attendance`}
          className="performance-link"
          state={{ 
            studentId: student._id || studentId, 
            studentName: student.name,
            halaqaId: contextHalaqaId,
            halaqaName: contextHalaqaName
          }}
        >
          <h3 className="performance-link-title">Attendance</h3>
          <p className="performance-link-subtitle">
            {contextHalaqaId ? `View attendance for this student in ${contextHalaqaName}` : 'View attendance history'}
          </p>
        </Link>
      </div>

      {/* Actions Section */}
      <h2 className="section-title">Actions</h2>
      <div className="actions-container">
        <button 
          className="action-button"
          onClick={handleAssignWork}
        >
          New Assignment
        </button>
        
        <button 
          className="action-button"
          onClick={handleContactParent}
        >
          Contact Parent
        </button>
      </div>

      {/* Notes Section */}
      <h2 className="section-title">Notes</h2>
      <div className="notes-container">
        <textarea
          className="notes-input"
          placeholder="Add notes about this student..."
          value={notes}
          onChange={(e) => handleSaveNotes(e.target.value)}
        ></textarea>
        {savingNotes && (
          <div className="saving-indicator">
            <div className="saving-spinner"></div>
            <span>Saving...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProfilePage;